// components/ChatbotTemplates.tsx
import React, { useState } from 'react';
import { FileText, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

interface Template {
  id: string;
  name: string;
  trigger: string;
  response: string;
  category: string;
  enabled: boolean;
}

export const ChatbotTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([
    {
      id: '1',
      name: 'Powitanie',
      trigger: 'cześć|witaj|dzień dobry|hello',
      response: 'Cześć! 👋 Miło Cię widzieć! W czym mogę pomóc? Interesujesz się produktami z pleksi?',
      category: 'Powitania',
      enabled: true
    },
    {
      id: '2',
      name: 'Pytanie o cenę',
      trigger: 'cena|koszt|ile kosztuje|cennik',
      response: 'Ceny naszych produktów zależą od rozmiaru, grubości materiału i ilości. Podstawowe osłony biurowe zaczynają się od 150 PLN. Chcesz otrzymać dokładną wycenę?',
      category: 'Cennik',
      enabled: true
    },
    {
      id: '3',
      name: 'Czas realizacji',
      trigger: 'termin|kiedy|jak długo|czas realizacji',
      response: 'Standardowy czas realizacji to 2-5 dni roboczych dla produktów typowych. Zamówienia niestandardowe realizujemy w 5-15 dni roboczych. Potrzebujesz szybszej realizacji?',
      category: 'Logistyka',
      enabled: true
    },
    {
      id: '4',
      name: 'Kontakt',
      trigger: 'kontakt|telefon|email|adres',
      response: 'Możesz się z nami skontaktować:\n📞 Tel: 884 042 107\n📧 Email: biuro@plexisystem.pl\n📍 Adres: Ks. Dr. Leona Heyke 11, 84-206 Nowy Dwór Wejherowski\n\nChcesz, żeby nasz handlowiec do Ciebie zadzwonił?',
      category: 'Kontakt',
      enabled: true
    }
  ]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState<Partial<Template>>({
    category: 'Inne'
  });
  const [showNewForm, setShowNewForm] = useState(false);

  const categories = ['Powitania', 'Cennik', 'Produkty', 'Logistyka', 'Kontakt', 'Inne'];

  const handleSave = (template: Template) => {
    setTemplates(prev => prev.map(t => t.id === template.id ? template : t));
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('Czy na pewno chcesz usunąć ten szablon?')) {
      setTemplates(prev => prev.filter(t => t.id !== id));
    }
  };

  const handleAddNew = () => {
    if (!newTemplate.name || !newTemplate.trigger || !newTemplate.response) {
      alert('Wypełnij wszystkie pola');
      return;
    }

    const template: Template = {
      id: Date.now().toString(),
      name: newTemplate.name,
      trigger: newTemplate.trigger,
      response: newTemplate.response,
      category: newTemplate.category || 'Inne',
      enabled: true
    };

    setTemplates(prev => [...prev, template]);
    setNewTemplate({ category: 'Inne' });
    setShowNewForm(false);
  };

  const handleToggleEnabled = (id: string) => {
    setTemplates(prev => prev.map(t => 
      t.id === id ? { ...t, enabled: !t.enabled } : t
    ));
  };

  return (
    <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-500" />
          Szablony odpowiedzi
        </h2>
        <button
          onClick={() => setShowNewForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Dodaj szablon
        </button>
      </div>

      {/* Formularz nowego szablonu */}
      {showNewForm && (
        <div className="mb-6 p-4 bg-zinc-700 rounded-lg border border-zinc-600">
          <h3 className="font-semibold mb-4">Nowy szablon</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nazwa</label>
              <input
                type="text"
                value={newTemplate.name || ''}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white"
                placeholder="np. Pytanie o dostępność"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Wyzwalacze (oddzielone |)
              </label>
              <input
                type="text"
                value={newTemplate.trigger || ''}
                onChange={(e) => setNewTemplate({ ...newTemplate, trigger: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white"
                placeholder="np. dostępny|na stanie|magazyn"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Odpowiedź</label>
              <textarea
                value={newTemplate.response || ''}
                onChange={(e) => setNewTemplate({ ...newTemplate, response: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white"
                rows={3}
                placeholder="Treść automatycznej odpowiedzi..."
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Kategoria</label>
              <select
                value={newTemplate.category || 'Inne'}
                onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddNew}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Zapisz
              </button>
              <button
                onClick={() => {
                  setShowNewForm(false);
                  setNewTemplate({ category: 'Inne' });
                }}
                className="bg-zinc-600 hover:bg-zinc-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista szablonów */}
      <div className="space-y-4">
        {categories.map(category => {
          const categoryTemplates = templates.filter(t => t.category === category);
          if (categoryTemplates.length === 0) return null;

          return (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">{category}</h3>
              <div className="space-y-2">
                {categoryTemplates.map(template => (
                  <div
                    key={template.id}
                    className={`p-4 rounded-lg border transition-all ${
                      template.enabled 
                        ? 'bg-zinc-700 border-zinc-600' 
                        : 'bg-zinc-800 border-zinc-700 opacity-50'
                    }`}
                  >
                    {editingId === template.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={template.name}
                          onChange={(e) => handleSave({ ...template, name: e.target.value })}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white"
                        />
                        <input
                          type="text"
                          value={template.trigger}
                          onChange={(e) => handleSave({ ...template, trigger: e.target.value })}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm"
                        />
                        <textarea
                          value={template.response}
                          onChange={(e) => handleSave({ ...template, response: e.target.value })}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm"
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingId(null)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                          >
                            Zapisz
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="bg-zinc-600 hover:bg-zinc-500 text-white px-3 py-1 rounded text-sm"
                          >
                            Anuluj
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-white">{template.name}</h4>
                          <div className="flex items-center gap-2">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={template.enabled}
                                onChange={() => handleToggleEnabled(template.id)}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-zinc-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                            </label>
                            <button
                              onClick={() => setEditingId(template.id)}
                              className="p-1 hover:bg-zinc-600 rounded transition-colors"
                            >
                              <Edit2 className="w-4 h-4 text-gray-400" />
                            </button>
                            <button
                              onClick={() => handleDelete(template.id)}
                              className="p-1 hover:bg-zinc-600 rounded transition-colors"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-orange-400 mb-2">
                          Wyzwalacze: {template.trigger}
                        </p>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">
                          {template.response}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};